// components/UserSelector/UserSelector.jsx
import React from 'react'
import AsyncSelector from 'components/AsyncSelector'
import HighlightableText from 'components/HighlightableText'

/**
 * Selector específico para usuarios con configuración predefinida
 * 
 * @param {object} value - Usuario seleccionado actual
 * @param {function} onChange - Callback cuando cambia la selección
 * @param {boolean} disabled - Si está deshabilitado
 * @param {boolean} isClearable - Si se puede limpiar la selección
 * @param {string} placeholder - Texto placeholder
 * @param {object} additionalFilters - Filtros adicionales para la API
 * @param {boolean} showEmail - Si mostrar email en el label
 * @param {boolean} showPhone - Si mostrar teléfono en el label
 * @param {object} customStyles - Estilos personalizados
 */
const UserSelector = ({
	value = null,
	onChange = () => { },
	disabled = false,
	isClearable = true,
	placeholder = 'Buscar cliente...',
	additionalFilters = {},
	showEmail = true,
	showPhone = false,
	customStyles = {},
	...otherProps
}) => {

	// Función para formatear el label del usuario
	const formatUserLabel = (user) => {
		if (!user) return ''

		let label = ''

		// Nombre completo
		if (user.nombre || user.apellido) {
			label = `${user.nombre || ''} ${user.apellido || ''}`.trim()
		}

		// Agregar email si está disponible y se solicita
		if (showEmail && user.email) {
			label += label ? ` - ${user.email}` : user.email
		}

		// Agregar teléfono si está disponible y se solicita
		if (showPhone && user.telefono) {
			label += label ? ` (${user.telefono})` : user.telefono
		}

		// Fallback si no hay información
		if (!label) {
			label = user.usuario || `Usuario #${user.id}` || 'Usuario desconocido'
		}

		return label
	}

	// Función para formatear cómo se ve cada opción en el dropdown
	const formatUserOption = (option, { context }) => {
		if (!option || !option.data) return null

		const user = option.data
		const searchTerm = option.searchTerm || ''

		return (
			<div className="user-option">
				<div className="user-option-main">
					<HighlightableText
						text={formatUserLabel(user)}
						highlight={searchTerm}
						className="user-option-label"
					/>
				</div>

				{/* Información adicional en una segunda línea */}
				{(user.telefono || user.usuario) && (
					<div className="user-option-meta">
						{user.telefono && (
							<small className="text-muted me-3">
								<i className="fa fa-phone me-1"></i>
								<HighlightableText
									text={user.telefono}
									highlight={searchTerm}
									highlightClassName="highlighted-text subtle"
								/>
							</small>
						)}

						{user.usuario && (
							<small className="text-muted">
								<i className="fa fa-user me-1"></i>
								<HighlightableText
									text={user.usuario}
									highlight={searchTerm}
									highlightClassName="highlighted-text subtle"
								/>
							</small>
						)}
					</div>
				)}
			</div>
		)
	}

	// Estilos específicos para el selector de usuarios
	const userStyles = {
		option: (provided, state) => ({
			...provided,
			padding: '8px 12px',
			backgroundColor: state.isFocused ? '#f8f9fa' : 'white',
			borderBottom: '1px solid #f0f0f0',
			cursor: 'pointer',
			'&:hover': {
				backgroundColor: '#e9ecef'
			}
		}),
		...customStyles
	}

	return (
		<AsyncSelector
			endpoint="a/ver-usuarios"
			searchFields={['usuario', 'nombre', 'apellido', 'email', 'telefono']}
			valueField="id"
			labelFormatter={formatUserLabel}
			optionFormatter={formatUserOption}
			placeholder={placeholder}
			value={value}
			onChange={onChange}
			disabled={disabled}
			isClearable={isClearable}
			additionalFilters={additionalFilters}
			minSearchLength={2}
			maxResults={20}
			noOptionsMessage="No se encontraron clientes"
			loadingMessage="Buscando clientes..."
			customStyles={userStyles}
			{...otherProps}
		/>
	)
}

export default UserSelector